import { useState } from 'react';
import { useI18n } from '../i18n/i18n';
import { useSEO } from '../hooks/useSEO';
import { getApiBaseUrl } from '../lib/utils';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { MapPin } from "lucide-react";

const formSchema = z.object({
  email: z.string().email(),
  subject: z.string().min(3).max(100),
  message: z.string().min(10).max(1000),
});

type FormValues = z.infer<typeof formSchema>;

const ContactPage: React.FC = () => {
  const { t } = useI18n();

  // SEO Configuration
  const seoContent = useSEO('main', 'contact');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      subject: '',
      message: '',
    },
  });

  const onSubmit = async (data: FormValues) => {
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch(`${getApiBaseUrl()}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setSubmitStatus('success');
        form.reset();
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container mx-auto py-12 px-4 md:px-6">
      {seoContent}
      <h1 className="text-4xl font-bold mb-8">{t('contact.title')}</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
        <div>
          <h2 className="text-2xl font-bold mb-4">{t('contact.getInTouch')}</h2>
          <p className="mb-6">{t('contact.description')}</p>

          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold">{t('contact.email')}</h3>
              <p>finanzprocontact@gmail.com</p>
            </div>
            <div>
              <h3 className="text-lg font-semibold">{t('contact.linkedin')}</h3>
              <a href="https://linkedin.com/company/finanzly" className="text-blue-600 hover:underline">
                linkedin.com/company/finanzly
              </a>
            </div>
            <div>
              <h3 className="text-lg font-semibold">{t('contact.officeHours')}</h3>
              <p>{t('contact.hours')}</p>
            </div>
            <div>
              <h3 className="text-lg font-semibold">{t('contact.location')}</h3>
              <div className="flex items-start gap-2">
                <MapPin className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <p>{t('contact.address')}</p>
              </div>
            </div>
          </div>
        </div>

        <div>
          <h2 className="text-2xl font-bold mb-4">{t('contact.sendMessage')}</h2>

          {submitStatus === 'success' && (
            <Alert className="mb-6 bg-green-50 border-green-500">
              <AlertDescription className="text-green-700">
                {t('contact.successMessage')}
              </AlertDescription>
            </Alert>
          )}

          {submitStatus === 'error' && (
            <Alert className="mb-6 bg-red-50 border-red-500">
              <AlertDescription className="text-red-700">
                {t('contact.errorMessage')}
              </AlertDescription>
            </Alert>
          )}

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('contact.form.email')}</FormLabel>
                    <FormControl>
                      <Input {...field} type="email" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="subject"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('contact.form.subject')}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="message"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('contact.form.message')}</FormLabel>
                    <FormControl>
                      <Textarea {...field} rows={5} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" disabled={isSubmitting} className="w-full">
                {isSubmitting ? t('contact.form.sending') : t('contact.form.send')}
              </Button>
            </form>
          </Form>
        </div>
      </div>

      {/* Map Section */}
      <div className="mt-12">
        {/* Header */}
        <div className="flex items-center gap-2 mb-4">
          <MapPin className="h-5 w-5 text-orange-500" />
          <h2 className="text-xl font-semibold text-gray-800">{t('contact.location')}</h2>
        </div>
        
        {/* Map Container */}
        <div className="rounded-lg overflow-hidden border shadow-sm">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3196.5!2d-6.3505!3d36.7725!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd0dd01e0b9a0c01%3A0x0!2sC.%20Nav%C3%ADo%2C%203%2C%2011540%20Sanl%C3%BAcar%20de%20Barrameda%2C%20C%C3%A1diz!5e0!3m2!1ses!2ses!4v1703800000000!5m2!1ses!2ses"
            width="100%"
            height="400"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Office Location Map"
            className="w-full"
          />
        </div>

        {/* Address Box - Separate */}
        <div className="mt-4 rounded-lg border shadow-sm bg-gray-50 px-4 py-3">
          <div className="flex items-start gap-2">
            <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0 text-orange-500" />
            <div>
              <p className="font-semibold text-gray-800">Finanzly</p>
              <p className="text-sm text-gray-600">{t('contact.address')}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;